import React, { useState } from 'react';
import { Calculator, Plus, Save, Download, Printer } from 'lucide-react';
import { Quote, QuoteLineItem as QuoteLineItemType } from '../../types/calculator';
import { QuoteLineItem } from './QuoteLineItem';
import { usePricing } from '../../contexts/PricingContext';
import {
  getAllPlanOptions,
  calculateLineItemSubtotal,
  calculateQuoteTotal,
  calculateAnnualTotal,
  formatCurrency,
  formatPlanOptionLabel,
  generateQuoteId,
  saveQuoteToLocalStorage,
  exportQuoteToText
} from '../../utils/calculatorUtils';

export const CostCalculator: React.FC = () => {
  const { period } = usePricing();
  const [quoteName, setQuoteName] = useState('New Quote');
  const [items, setItems] = useState<QuoteLineItemType[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [showSaved, setShowSaved] = useState(false);

  const planOptions = getAllPlanOptions(period);
  const monthlyTotal = calculateQuoteTotal(items);
  const annualTotal = calculateAnnualTotal(monthlyTotal);

  const addLineItem = () => {
    if (!selectedPlanId) return;

    const plan = planOptions.find(p => p.id === selectedPlanId);
    if (!plan) return;

    const existingItem = items.find(item => item.planId === plan.id);
    if (existingItem) {
      setItems(items.map(item =>
        item.id === existingItem.id
          ? {
              ...item,
              quantity: item.quantity + 1,
              subtotal: item.quotable && item.pricePerUser !== undefined
                ? calculateLineItemSubtotal(item.pricePerUser, item.quantity + 1)
                : 0,
            }
          : item
      ));
    } else {
      const newItem: QuoteLineItemType = {
        id: `item-${Date.now()}`,
        planId: plan.id,
        planName: plan.name,
        priceLabel: plan.priceLabel,
        pricePerUser: plan.price,
        quantity: 1,
        subtotal: plan.quotable && plan.price !== undefined ? plan.price : 0,
        quotable: plan.quotable,
      };
      setItems([...items, newItem]);
    }

    setSelectedPlanId('');
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) return;

    setItems(items.map(item =>
      item.id === itemId
        ? {
            ...item,
            quantity,
            subtotal: item.quotable && item.pricePerUser !== undefined
              ? calculateLineItemSubtotal(item.pricePerUser, quantity)
              : 0,
          }
        : item
    ));
  };

  const removeItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const saveQuote = () => {
    const quote: Quote = {
      id: generateQuoteId(),
      name: quoteName,
      items,
      total: monthlyTotal,
      billingCycle,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    saveQuoteToLocalStorage(quote);
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  const exportQuote = () => {
    const quote: Quote = {
      id: generateQuoteId(),
      name: quoteName,
      items,
      total: monthlyTotal,
      billingCycle,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const text = exportQuoteToText(quote);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${quoteName.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printQuote = () => {
    const quote: Quote = {
      id: generateQuoteId(),
      name: quoteName,
      items,
      total: monthlyTotal,
      billingCycle,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const text = exportQuoteToText(quote);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Quote</title>');
      printWindow.document.write('<style>body { font-family: monospace; padding: 20px; white-space: pre-wrap; }</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(text);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calculator className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Cost Calculator</h2>
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="quote-name" className="block text-sm font-medium text-gray-700 mb-2">
          Quote Name
        </label>
        <input
          id="quote-name"
          type="text"
          value={quoteName}
          onChange={(e) => setQuoteName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter quote name"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="plan-select" className="block text-sm font-medium text-gray-700 mb-2">
          Add License Plan
        </label>
        <div className="flex gap-2">
          <select
            id="plan-select"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a plan...</option>
            {planOptions.map(plan => (
              <option key={plan.id} value={plan.id}>
                {formatPlanOptionLabel(plan)}
              </option>
            ))}
          </select>
          <button
            onClick={addLineItem}
            disabled={!selectedPlanId}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {items.length > 0 && (
        <>
          <div className="space-y-3 mb-6">
            {items.map(item => (
              <QuoteLineItem
                key={item.id}
                item={item}
                onQuantityChange={updateQuantity}
                onRemove={removeItem}
              />
            ))}
          </div>

          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-gray-700">Billing Cycle</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingCycle === 'monthly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    billingCycle === 'annual'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Annual
                </button>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-700">Monthly Total:</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {formatCurrency(monthlyTotal)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-700">Annual Total:</span>
                  <span className="text-xl font-semibold text-gray-900">
                    {formatCurrency(annualTotal)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={saveQuote}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Quote
            </button>
            <button
              onClick={exportQuote}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={printQuote}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>

          {showSaved && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800 font-medium">Quote saved successfully!</p>
            </div>
          )}
        </>
      )}

      {items.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Add license plans to start building your quote</p>
        </div>
      )}
    </div>
  );
};
